'use client';
import type React from 'react';
import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { isWindowDefined } from '@core/Constants/Window';
import { Share } from '@gds/Icons/Paths/Share';
import { Typography } from '@gds/Typography/Typography';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { generateShareButtonTracking } from './ShareButton.tracking';

import styles from './ShareButton.module.css';

const NOTIF_TIMEOUT = 3000;

export const ShareButton = () => {
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const [shouldShowNotif, setShouldShowNotif] = useState(false);
  const [hasShareFn, setHasShareFn] = useState(false);
  const [hasCopyFn, setHasCopyFn] = useState(false);
  const currentPageUrl = isWindowDefined ? window.location.href : '';
  const shareUrl = currentPageUrl ? new URL(currentPageUrl) : '';

  if (typeof shareUrl === 'object') {
    shareUrl.searchParams.append('utm_source', 'pdp_share');
  }

  useEffect(() => {
    // @ts-expect-error //says "condition will always return true since this function is always defined" but that is not true
    if (window.navigator?.share) {
      setHasShareFn(true);
    }
    if (window.navigator?.clipboard) {
      setHasCopyFn(true);
    }
  }, []);

  if (!hasShareFn && !hasCopyFn) return null;

  const trackingFunction = () => {
    const label = hasShareFn ? 'share' : 'share not supported';

    const { event, context } = generateShareButtonTracking({
      searchParams,
      userId,
      formState,
      ctaType: 'button',
      ctaWording: label,
      ctaUrl: shareUrl.toString(),
    });

    trackCustomEvent({ event, context });
  };

  const preventClick = (e: React.MouseEvent<HTMLDivElement | HTMLOrSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = () => {
    if (hasShareFn) {
      navigator?.share({
        url: currentPageUrl,
      });
    } else if (hasCopyFn) {
      navigator?.clipboard.writeText(currentPageUrl);
      setShouldShowNotif(true);
    }

    trackingFunction();
  };

  const hideNotif = () => {
    setTimeout(() => {
      setShouldShowNotif(false);
    }, NOTIF_TIMEOUT);
  };

  return (
    <>
      <Typography
        className={`${styles.notif} ${shouldShowNotif ? styles.show : ''}`}
        Component="p"
        onTransitionEnd={() => hideNotif()}
        variant="caption1"
      >
        {'copied to clipboard'}
      </Typography>
      <div onClick={preventClick} data-test-id="share-button">
        <Share onClick={handleClick} />
      </div>
    </>
  );
};
